// src/components/comments/CommentComposer.jsx
import React, { useEffect, useRef, useState } from 'react';
import '../../styles/CommentSidebar.css';

const CHAR_LIMIT = 300;

function sortSegments(a, b) {
  if (a.page !== b.page) return a.page - b.page;
  const ay = a.rects?.[0]?.y ?? 0, by = b.rects?.[0]?.y ?? 0;
  if (ay !== by) return ay - by;
  const ax = a.rects?.[0]?.x ?? 0, bx = b.rects?.[0]?.x ?? 0;
  return ax - bx;
}

export default function CommentComposer({
  onSubmit,
  onBeginQuotePicking,
  onEndQuotePicking,
  onRegisterQuoteAppend,
  onComposeOpen,
  onComposeClose,
  showForm,          // controlled
  setShowForm,       // controlled
  hideTrigger,       // when true, render nothing while closed (header owns the trigger)
  seedQuote,         // text to pre-fill as the quote (from the editor selection chip)
  onSeedConsumed,    // called once the seed has been applied, so parent can reset it
}) {
  const [quote, setQuote] = useState('');
  const [segments, setSegments] = useState([]); // [{text,page,rects}]
  const [text, setText] = useState('');

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [picking, setPicking] = useState(false);

  const pickingRef = useRef(false);
  const textRef = useRef(null);

  // Auto-focus the comment field whenever the form opens, so the user can
  // start typing immediately (unless they're actively picking a quote).
  useEffect(() => {
    if (showForm && !picking) {
      const id = setTimeout(() => textRef.current?.focus(), 80);
      return () => clearTimeout(id);
    }
  }, [showForm, picking]);

  function buildQuote(newSegments) {
    const sorted = [...newSegments].sort(sortSegments);
    let out = '';
    for (const s of sorted) {
      if (!s.text) continue;
      const sep = out ? ' … ' : '';
      const remaining = CHAR_LIMIT - out.length - sep.length;
      if (remaining <= 0) break;
      const chunk = s.text.slice(0, remaining);
      out += sep + chunk;
      if (chunk.length < s.text.length) break;
    }
    return out;
  }

  function appendSelection(sel) {
    if (!sel?.text) return;
    setSegments(prev => {
      const next = [...prev, sel].sort(sortSegments);
      const built = buildQuote(next);
      setQuote(built);
      if (built.length >= CHAR_LIMIT) {
        pickingRef.current = false;
        setPicking(false);
        onEndQuotePicking?.();
      }
      return next;
    });
  }

  // Register append callback with the reader
  useEffect(() => {
    onRegisterQuoteAppend?.(appendSelection);
    return () => onRegisterQuoteAppend?.(null);
  }, [onRegisterQuoteAppend]);

  // Seed the quote from the editor's selection chip. Replaces any current quote
  // with the selected text, opens the form, then tells the parent to clear the
  // seed (so the same text can be selected again later).
  useEffect(() => {
    const seed = (seedQuote || '').trim();
    if (!seed) return;
    const clipped = seed.slice(0, CHAR_LIMIT);
    setSegments([{ text: clipped, page: null, rects: [] }]);
    setQuote(clipped);
    setShowForm(true);
    onSeedConsumed?.();
  }, [seedQuote]); // eslint-disable-line react-hooks/exhaustive-deps

  function startPicking() {
    if (!onBeginQuotePicking) return;
    setErr('');
    setPicking(true);
    pickingRef.current = true;
    onBeginQuotePicking();
  }

  function stopPicking() {
    pickingRef.current = false;
    setPicking(false);
    onEndQuotePicking?.();
  }

  function clearQuote() {
    setSegments([]);
    setQuote('');
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = (text || '').trim();
    if (!body) return;

    try {
      setBusy(true);
      setErr('');
      // Only build a rect-based highlight from segments that actually carry
      // rects (reader quote-picking). A seeded editor selection has no rects —
      // its quote text alone drives the editor's comment highlighting.
      const rectSegments = segments.filter((s) => s.rects && s.rects.length);
      await onSubmit?.(body, {
        quote: quote || null,
        highlight: rectSegments.length
          ? { segments: rectSegments.map(s => ({ page: s.page, rects: s.rects })) }
          : null,
      });
      clearQuote();
      setText('');
      setShowForm(false);
      onComposeClose?.();
    } catch (ex) {
      setErr(ex?.message || 'Failed to post comment.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="comment-composer">
      {showForm ? (
        <form onSubmit={handleSubmit}>
          <label className="row between">
            <span>Quoted Passage:</span>
            {!picking ? (
              <div className="row gap-8">
                <button
                  type="button"
                  className="btn-outline"
                  onClick={startPicking}
                  disabled={busy}
                  title="Click, then select text anywhere in the page; repeat to add more snippets"
                >
                  Highlight Quote
                </button>
                {quote && (
                  <button type="button" className="btn-outline" onClick={clearQuote} disabled={busy}>
                    Clear
                  </button>
                )}
              </div>
            ) : (
              <div className="row gap-8">
                <span className="hint">Selecting… (Esc to cancel)</span>
                <button type="button" className="btn-outline" onClick={stopPicking}>
                  Finish
                </button>
              </div>
            )}
          </label>

          <textarea
            value={quote}
            readOnly
            placeholder="Quoted Passage (read-only; use Highlight Quote)"
          />
          <div className="char-counter">{quote.length}/{CHAR_LIMIT}</div>

          {picking && (
            <div className="quote-done-row" style={{ margin: '6px 0 10px' }}>
              <button type="button" className="btn-outline" onClick={stopPicking}>
                Done
              </button>
            </div>
          )}

          <label>Your Comment:</label>
          <textarea
            ref={textRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write your comment…"
            required
          />

          <div className="composer-actions">
            <button type="submit" disabled={busy}>
              {busy ? 'Submitting…' : 'Submit'}
            </button>
            <button
              type="button"
              onClick={() => {
                if (pickingRef.current) stopPicking();
                setShowForm(false);
                onComposeClose?.();
              }}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
          {err && <div className="composer-error">{err}</div>}
        </form>
      ) : hideTrigger ? null : (
        <button
          className="comment-book-btn"
          onClick={() => { setShowForm(true); onComposeOpen?.(); }}
        >
          Comment on the Book
        </button>
      )}
    </div>
  );
}
